import { loadJwt } from '../auth/mobileMpcService';
import { baseUrl, apiVersion } from '../../utils/dwclient/dwcommonutils';
import { encodeSendObjectToBase58 } from '../../utils/dwclient/jwtcomm';
import { getKeySignature as getDesktopKeySignature } from '../../utils/dataEncryption';
import { getKeySignature as getMobileKeySignature } from '../../mobile/auth/mobileEncryption';
import { attemptAutoRefreshResult, captureRefreshBinding, isRefreshBindingCurrent } from '../auth/authRefreshManager';
export const getOrchestratorBaseUrl = () => {
    let url = '/orchestrator';
    if (typeof process !== 'undefined') {
        if (process.env?.ORCHESTRATOR_URL) {
            url = process.env.ORCHESTRATOR_URL;
        }
        else if (process.env?.REACT_APP_ORCHESTRATOR_URL) {
            url = process.env.REACT_APP_ORCHESTRATOR_URL;
        }
    }
    if (url === '/orchestrator') {
        url = 'https://dworch.interb2b.com';
    }
    console.log('[Upload] Orchestrator base URL:', url);
    return url;
};
const getHeaders = async () => {
    const token = await loadJwt();
    const headers = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};
async function parseJsonResponse(response, fallbackError) {
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        const text = await response.text().catch(() => '');
        if (text.trimStart().startsWith('<')) {
            throw new Error('Upload service is not available. Please try again later.');
        }
        throw new Error(fallbackError);
    }
    return response.json();
}
async function fetchWithAuthRetry(url, init) {
    const authorization = new Headers(init.headers).get('Authorization');
    const requestBinding = captureRefreshBinding(authorization?.startsWith('Bearer ') ? authorization.slice(7) : null);
    let response = await fetch(url, init);
    if (response.status === 401 && requestBinding && isRefreshBindingCurrent(requestBinding)) {
        const refresh = await attemptAutoRefreshResult();
        if (refresh.status === 'success') {
            const retryHeaders = { ...init.headers };
            retryHeaders['Authorization'] = `Bearer ${refresh.jwt}`;
            response = await fetch(url, { ...init, headers: retryHeaders });
        }
    }
    return response;
}
export async function checkOrchestratorAvailable() {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const healthUrl = `${orchBaseUrl}/health`;
    console.log('[Upload] Checking orchestrator availability at:', healthUrl);
    try {
        const startTime = Date.now();
        const response = await fetch(healthUrl, {
            method: 'GET',
            signal: AbortSignal.timeout(15000),
        });
        const elapsed = Date.now() - startTime;
        const contentType = response.headers.get('content-type') || '';
        console.log('[Upload] Health check response:', {
            url: healthUrl,
            status: response.status,
            ok: response.ok,
            elapsed: `${elapsed}ms`,
            contentType,
        });
        if (!response.ok) {
            return false;
        }
        if (!contentType.includes('application/json')) {
            const text = await response.text().catch(() => '');
            console.warn('[Upload] Health check returned non-JSON (likely HTML proxy page):', text.substring(0, 200));
            return false;
        }
        try {
            const body = await response.json();
            console.log('[Upload] Health check body:', body);
            if (body.status === 'ok') {
                return true;
            }
        }
        catch {
            console.warn('[Upload] Could not parse health response as JSON');
            return false;
        }
        return true;
    }
    catch (error) {
        console.error('[Upload] Health check failed:', {
            url: healthUrl,
            error: error instanceof Error ? error.message : String(error),
        });
        return false;
    }
}
export async function initBatch(files, graphId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/init`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({
            files: files.map(f => ({
                name: f.name,
                path: f.path,
                size: f.size,
            })),
            graphId,
        }),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to initialize batch');
    }
    return parseJsonResponse(response, 'Failed to initialize batch');
}
export async function getNextFile(batchId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/next`, {
        method: 'GET',
        headers: await getHeaders(),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to get next file');
    }
    return parseJsonResponse(response, 'Failed to get next file');
}
export async function completeFile(batchId, fileIndex, uploadedBytes) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/complete/${fileIndex}`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({ uploadedBytes }),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to complete file');
    }
}
export async function failFile(batchId, fileIndex, error, retry = false) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/fail/${fileIndex}`, {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify({ error, retry }),
    });
    if (!response.ok) {
        const err = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(err.error || 'Failed to mark file as failed');
    }
}
export async function getBatchStatus(batchId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/status`, {
        method: 'GET',
        headers: await getHeaders(),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to get batch status');
    }
    return parseJsonResponse(response, 'Failed to get batch status');
}
export async function pauseBatch(batchId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/pause`, {
        method: 'POST',
        headers: await getHeaders(),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to pause batch');
    }
}
export async function resumeBatch(batchId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/resume`, {
        method: 'POST',
        headers: await getHeaders(),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to resume batch');
    }
}
export async function cancelBatch(batchId) {
    const orchBaseUrl = getOrchestratorBaseUrl();
    const response = await fetchWithAuthRetry(`${orchBaseUrl}/batch/${batchId}/cancel`, {
        method: 'POST',
        headers: await getHeaders(),
    });
    if (!response.ok) {
        const error = await parseJsonResponse(response, response.statusText).catch(() => ({ error: response.statusText }));
        throw new Error(error.error || 'Failed to cancel batch');
    }
}
async function encryptFileForUpload(file) {
    const desktopSig = getDesktopKeySignature();
    const mobileSig = getMobileKeySignature();
    const signature = desktopSig || mobileSig;
    console.log('[Upload] encryptFileForUpload:', {
        fileName: file.name,
        fileSize: file.size,
        hasDesktopKey: !!desktopSig,
        hasMobileKey: !!mobileSig,
        signatureSource: desktopSig ? 'desktop(sessionStorage)' : mobileSig ? 'mobile(sessionStorage)' : 'NONE',
        signaturePrefix: signature ? signature.substring(0, 10) + '...' : 'null',
    });
    if (!signature) {
        console.warn('[Upload] No encryption key available, uploading without encryption');
        return file;
    }
    const hashBuffer = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(signature));
    const keyMaterial = new Uint8Array(hashBuffer);
    const symmetricKey = await crypto.subtle.importKey("raw", keyMaterial, { name: "AES-GCM" }, true, ["encrypt"]);
    const fileBuffer = await file.arrayBuffer();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, symmetricKey, fileBuffer);
    const result = new Uint8Array(12 + encrypted.byteLength);
    result.set(iv, 0);
    result.set(new Uint8Array(encrypted), 12);
    console.log('[Upload] File encrypted successfully:', {
        fileName: file.name,
        originalSize: file.size,
        encryptedSize: result.byteLength,
        ivHex: Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join(''),
    });
    return new File([result], file.name, { type: file.type, lastModified: file.lastModified });
}
export async function uploadFileToStorage(file, targetPath) {
    const token = await loadJwt();
    if (!token) {
        return { success: false, error: 'Not authenticated' };
    }
    const sendObject = { path: targetPath };
    const message = encodeSendObjectToBase58(sendObject);
    const encryptedFile = await encryptFileForUpload(file);
    const formData = new FormData();
    formData.append('file', encryptedFile);
    formData.append('message', message);
    try {
        const url = `${baseUrl}/api/${apiVersion}/files/write`;
        const response = await fetchWithAuthRetry(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            return { success: false, error: error.error || error.message || 'Upload failed' };
        }
        return { success: true };
    }
    catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Upload failed' };
    }
}
export class UploadProgressWebSocket {
    ws = null;
    batchId;
    onProgress;
    onError;
    onClose;
    reconnectAttempts = 0;
    maxReconnectAttempts = 3;
    pingInterval = null;
    constructor(batchId, onProgress, onError, onClose) {
        this.batchId = batchId;
        this.onProgress = onProgress;
        this.onError = onError;
        this.onClose = onClose;
    }
    connect() {
        const orchBaseUrl = getOrchestratorBaseUrl();
        if (orchBaseUrl.startsWith('/')) {
            console.log(`[Upload] WebSocket skipped for batch ${this.batchId} (proxy mode - using HTTP callbacks)`);
            return;
        }
        const wsUrl = `${orchBaseUrl.replace(/^http/, 'ws')}/ws/batch/${this.batchId}/progress`;
        console.log(`[Upload] WebSocket connecting to: ${wsUrl}`);
        try {
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                console.log(`[Upload] WebSocket connected for batch ${this.batchId}`);
                this.reconnectAttempts = 0;
                this.startPingInterval();
            };
            this.ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    this.onProgress(data);
                }
                catch (error) {
                    console.error('[Upload] WebSocket message parse error:', error);
                }
            };
            this.ws.onerror = () => {
                console.warn('[Upload] WebSocket error (will fall back to HTTP polling)');
            };
            this.ws.onclose = (event) => {
                this.stopPingInterval();
                console.log(`[Upload] WebSocket closed for batch ${this.batchId}, code: ${event.code}`);
                if (event.code !== 1000 && event.code !== 1001) {
                    console.log('[Upload] WebSocket unavailable - using HTTP polling for progress');
                }
            };
        }
        catch (error) {
            console.warn('[Upload] Failed to establish WebSocket connection:', error);
        }
    }
    startPingInterval() {
        this.pingInterval = window.setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(JSON.stringify({ type: 'ping' }));
            }
        }, 30000);
    }
    stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }
    requestStatus() {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'status' }));
        }
    }
    disconnect() {
        this.stopPingInterval();
        this.maxReconnectAttempts = 0;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
}
export function formatBytes(bytes) {
    if (bytes === 0)
        return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
